import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  ShieldCheck,
  Lock,
  CheckCircle2,
  Download,
  FileText,
} from "lucide-react";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:border-emerald-500 text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400";
const labelClass = "block text-xs font-bold text-slate-700 uppercase mb-1";

const initialForm = {
  navn: "",
  epost: "",
  telefon: "",
  adresse: "",
  postnummer: "",
  poststed: "",
  maalepunkt_id: "",
  maalernummer: "",
  uten_maalerinfo: false,
  godtar_vilkar: false,
};

const benefits = [
  "0 øre påslag – du betaler det strømmen koster",
  "49 kr fast månedsavgift – punktum",
  "Ingen bindingstid – si opp når du vil",
  "Ingen oppsigelsesgebyr",
  "Ingen skjulte kostnader eller tilleggstjenester",
  "Fungerer sammen med Norgespris",
  "100 % fornybar energi med opprinnelsesgaranti",
];

const steps = [
  { n: "1", t: "Fyll ut skjemaet", d: "Navn, kontaktinfo og adresse – tar to minutter." },
  { n: "2", t: "Vi ordner byttet", d: "Vi sier opp den gamle avtalen din og gjennomfører byttet." },
  { n: "3", t: "Du får dokumentene", d: "Avtale med dine opplysninger og angreskjema sendes til e-posten din med en gang." },
];

const BliKundePage = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [bankidNavn, setBankidNavn] = useState(null);
  const [bankidFeil, setBankidFeil] = useState("");
  const [signeringKlar, setSigneringKlar] = useState(false);
  const [signerer, setSignerer] = useState(false);

  useEffect(() => {
    document.title = "Bli kunde – Rstrøm Privat | 0 påslag, 49 kr i måneden";
    // Synlig tilbakemelding etter BankID-forsøk
    const params = new URLSearchParams(window.location.search);
    if (params.get("bankid") === "feilet") {
      const aarsak = params.get("aarsak") || "";
      const tekst = aarsak === "access_denied" || aarsak === "avbrutt"
        ? "BankID-innloggingen ble avbrutt. Prøv igjen når du er klar."
        : aarsak === "missing_product"
          ? "BankID er ikke aktivert på kontoen ennå (kode: missing_product). Vi ordner dette – prøv igjen snart."
          : aarsak === "token"
            ? "Kunne ikke fullføre BankID-innloggingen (kode: token). Prøv igjen."
            : aarsak === "profil"
              ? "Innloggingen gikk, men profilen manglet (kode: profil). Prøv igjen."
              : `BankID-innloggingen feilet${aarsak ? ` (kode: ${aarsak})` : ""}. Prøv igjen.`;
      setBankidFeil(tekst);
      toast.error("BankID-innlogging", { description: tekst, duration: 15000 });
      window.history.replaceState({}, "", "/bli-kunde");
    } else if (params.get("innlogget") === "bankid") {
      window.history.replaceState({}, "", "/bli-kunde");
      toast.success("Innlogget med BankID", { description: "Du er nå logget inn – navnet ditt er fylt ut." });
    }
    axios.get(`${API}/bankid/status`)
      .then((r) => setSigneringKlar(!!r.data.signering))
      .catch(() => {});
    axios.get(`${API}/auth/meg`, { withCredentials: true })
      .then((r) => {
        setBankidNavn(r.data.navn);
        setForm((p) => ({ ...p, navn: p.navn || r.data.navn }));
      })
      .catch(() => {});
  }, []);

  const update = (field) => (e) =>
    setForm((prev) => ({
      ...prev,
      [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const handleBankId = async () => {
    try {
      const st = await axios.get(`${API}/bankid/status`);
      if (st.data.konfigurert) {
        window.location.assign(`${API}/auth/bankid/login?return_to=/bli-kunde`);
        return;
      }
      toast.info("BankID-innlogging kobles til", {
        description: "Vi kobler til BankID for tiden. Fyll ut skjemaet under – vi ordner resten manuelt.",
      });
      document.getElementById("bestill-skjema")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      toast.info("BankID-innlogging kobles til", {
        description: "Fyll ut skjemaet under i mellomtiden – vi ordner resten manuelt.",
      });
      document.getElementById("bestill-skjema")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const startSignering = async () => {
    setSignerer(true);
    try {
      const r = await axios.post(`${API}/bestill/${done.ordre_id}/signering`);
      window.location.assign(r.data.signature_url);
    } catch (err) {
      toast.error("Kunne ikke starte signeringen", {
        description: err.response?.data?.detail || "Prøv igjen, eller last ned dokumentene under.",
      });
      setSignerer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form };
      if (payload.uten_maalerinfo) {
        payload.maalepunkt_id = "";
        payload.maalernummer = "";
      }
      const res = await axios.post(`${API}/bestill`, payload);
      setDone(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Noe gikk galt", {
        description: err.response?.data?.detail || "Bestillingen kunne ikke sendes. Prøv igjen.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid="bli-kunde-page">
      <header className="sticky top-0 z-50 bg-[#0B132B]/95 backdrop-blur-md border-b border-white/10 text-white">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" aria-label="Til forsiden" data-testid="bli-kunde-logo-link">
            <Logo />
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors"
            data-testid="bli-kunde-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Til forsiden
          </a>
        </div>
      </header>

      {done ? (
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-6 text-center" data-testid="order-success">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center text-emerald-600 mb-8">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-4">
              Takk for bestillingen!
            </h1>
            <p className="text-slate-600 leading-relaxed mb-2">
              Ordrenummer: <strong data-testid="order-id">{done.ordre_id}</strong>
            </p>
            <p className="text-slate-600 leading-relaxed mb-10">
              {done.epost_status === "sendt"
                ? "Avtaledokumentet med dine opplysninger, de fullstendige vilkårene og angreskjemaet er sendt til e-postadressen din."
                : "Dokumentene dine er klare – last dem gjerne ned under. De sendes også på e-post så snart e-posttjenesten er koblet til."}
            </p>
            {signeringKlar && (
              <div className="mb-10 p-7 rounded-2xl bg-[#0B132B] text-white" data-testid="signing-prompt">
                <h2 className="text-lg font-bold mb-2">Ett steg igjen: Signer avtalen din</h2>
                <p className="text-sm text-slate-300 mb-5 leading-relaxed">
                  Signer kundeavtalen trygt med BankID – det tar under ett minutt, og avtalen er
                  i kraft så snart signeringen er fullført.
                </p>
                <button
                  type="button"
                  onClick={startSignering}
                  disabled={signerer}
                  className="w-full sm:w-auto px-8 py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-[#0B132B] font-bold text-base transition-colors flex items-center justify-center gap-2"
                  data-testid="start-signing-button"
                >
                  <Lock className="w-5 h-5" />
                  {signerer ? "Starter signering ..." : "Signer avtalen med BankID"}
                </button>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { doc: "avtale.pdf", label: "Avtaledokument", testid: "download-avtale" },
                { doc: "vilkar.pdf", label: "Avtalevilkår", testid: "download-vilkar" },
                { doc: "angreskjema.pdf", label: "Angreskjema", testid: "download-angreskjema" },
              ].map((d) => (
                <a
                  key={d.doc}
                  href={`${API}/ordre/${done.ordre_id}/${d.doc}`}
                  className="p-5 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 transition-colors flex flex-col items-center gap-3"
                  data-testid={d.testid}
                >
                  <FileText className="w-8 h-8 text-emerald-600" />
                  <span className="text-sm font-bold text-[#0B132B] flex items-center gap-1.5">
                    <Download className="w-4 h-4" /> {d.label}
                  </span>
                </a>
              ))}
            </div>
            <a href="/" className="inline-block mt-10 text-sm font-semibold text-emerald-600 hover:text-emerald-500" data-testid="success-home-link">
              ← Tilbake til forsiden
            </a>
          </div>
        </section>
      ) : (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Infokolonne */}
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
                  Rstrøm Privat
                </span>
                <h1 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2 mb-6">
                  Strøm uten påslag. Enkelt og ærlig.
                </h1>
                <p className="text-slate-600 leading-relaxed mb-8">
                  Du betaler det strømmen faktisk koster – ikke et øre mer. Ingen
                  binding, ingen oppsigelsesgebyr, ingen skjulte kostnader. Bare
                  en lav, fast månedsavgift.
                </p>

                <div className="p-6 rounded-2xl bg-white border-2 border-emerald-500 shadow-lg mb-8" data-testid="bli-kunde-price-card">
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-sm text-slate-600">Påslag per kWh:</span>
                    <span className="text-2xl font-bold text-emerald-600">0 øre</span>
                  </div>
                  <div className="flex justify-between items-baseline mb-4">
                    <span className="text-sm text-slate-600">Fast månedsavgift:</span>
                    <span className="text-2xl font-bold text-[#0B132B]">49 kr</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-slate-600">Bindingstid / oppsigelse:</span>
                    <span className="text-sm font-bold text-emerald-600">Ingen / Ingen</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-10">
                  {benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
                      <BadgeCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="space-y-4">
                  {steps.map((s) => (
                    <div key={s.n} className="flex gap-4">
                      <div className="w-9 h-9 rounded-full bg-[#0B132B] text-emerald-400 flex items-center justify-center font-bold text-sm shrink-0">
                        {s.n}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-[#0B132B]">{s.t}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bestillingskort */}
              <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-2xl border-t-4 border-t-emerald-500" id="bestill-skjema">
                <h2 className="text-2xl font-bold text-[#0B132B] mb-2">Bestill Rstrøm Privat</h2>
                <p className="text-sm text-slate-600 mb-6">
                  Etter bestilling får du avtaledokumentet med dine opplysninger og
                  angreskjemaet tilsendt på e-post med en gang.
                </p>

                {bankidFeil && (
                  <div
                    className="w-full p-4 rounded-lg bg-red-50 border-2 border-red-300 text-red-800 text-sm font-semibold mb-2 flex items-start gap-3"
                    data-testid="bankid-feil-banner"
                  >
                    <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
                    <span>{bankidFeil}</span>
                  </div>
                )}
                {bankidNavn ? (
                  <div
                    className="w-full py-4 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-800 font-semibold text-sm flex items-center justify-center gap-2 mb-2"
                    data-testid="bankid-logget-inn-banner"
                  >
                    <BadgeCheck className="w-5 h-5 text-emerald-600" />
                    Innlogget med BankID som <strong>{bankidNavn}</strong>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleBankId}
                    className="w-full py-4 rounded-lg bg-[#0B132B] hover:bg-[#1a2547] text-white font-bold text-base transition-colors flex items-center justify-center gap-3 mb-2"
                    data-testid="bankid-login-button"
                  >
                    <Lock className="w-5 h-5" /> Logg inn med BankID
                  </button>
                )}
                <p className="text-[11px] text-slate-500 text-center mb-6">
                  Etter innlogging hentes navn, e-post og mobilnummer automatisk.
                </p>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-semibold uppercase">eller fyll ut manuelt</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <form className="space-y-4" onSubmit={handleSubmit} data-testid="order-form">
                  <div>
                    <label htmlFor="b_navn" className={labelClass}>Fullt navn</label>
                    <input id="b_navn" type="text" required placeholder="Navn Navnesen" className={inputClass} value={form.navn} onChange={update("navn")} data-testid="order-name-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="b_epost" className={labelClass}>E-postadresse</label>
                      <input id="b_epost" type="email" required placeholder="navn@epost.no" className={inputClass} value={form.epost} onChange={update("epost")} data-testid="order-email-input" />
                    </div>
                    <div>
                      <label htmlFor="b_telefon" className={labelClass}>Mobilnummer</label>
                      <input id="b_telefon" type="tel" required placeholder="Ditt mobilnummer" className={inputClass} value={form.telefon} onChange={update("telefon")} data-testid="order-phone-input" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="b_adresse" className={labelClass}>Adresse (dit strømmen skal leveres)</label>
                    <input id="b_adresse" type="text" required placeholder="Gateadresse 1" className={inputClass} value={form.adresse} onChange={update("adresse")} data-testid="order-address-input" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="b_postnr" className={labelClass}>Postnummer</label>
                      <input id="b_postnr" type="text" required placeholder="0581" className={inputClass} value={form.postnummer} onChange={update("postnummer")} data-testid="order-postal-input" />
                    </div>
                    <div>
                      <label htmlFor="b_poststed" className={labelClass}>Poststed</label>
                      <input id="b_poststed" type="text" required placeholder="Oslo" className={inputClass} value={form.poststed} onChange={update("poststed")} data-testid="order-city-input" />
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="text-xs font-bold text-slate-700 uppercase">Målerinformasjon (valgfritt)</div>
                        <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                          Du finner målepunkt-ID (18 siffer) og målernummer på strømfakturaen din.
                        </p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="b_mpid" className={labelClass}>Målepunkt-ID</label>
                        <input id="b_mpid" type="text" placeholder="18 siffer" className={inputClass} value={form.maalepunkt_id} onChange={update("maalepunkt_id")} disabled={form.uten_maalerinfo} data-testid="order-meterpoint-input" />
                      </div>
                      <div>
                        <label htmlFor="b_mnr" className={labelClass}>Målernummer</label>
                        <input id="b_mnr" type="text" placeholder="Står på fakturaen" className={inputClass} value={form.maalernummer} onChange={update("maalernummer")} disabled={form.uten_maalerinfo} data-testid="order-meterno-input" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-emerald-500" checked={form.uten_maalerinfo} onChange={update("uten_maalerinfo")} data-testid="order-no-meter-checkbox" />
                      Jeg finner ikke målerinformasjonen – fortsett uten
                    </label>
                  </div>

                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" required className="w-4 h-4 mt-0.5 accent-emerald-500" checked={form.godtar_vilkar} onChange={update("godtar_vilkar")} data-testid="order-terms-checkbox" />
                    <span>
                      Jeg har lest og godtar{" "}
                      <a href="/spotprisavtalevilkar-rstrom-privat.pdf" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline" data-testid="order-terms-link">
                        avtalevilkårene
                      </a>{" "}
                      og mottatt informasjon om angrerett.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-[#0B132B] font-bold text-base transition-colors shadow-md flex items-center justify-center gap-2"
                    data-testid="order-submit-button"
                  >
                    <ShieldCheck className="w-5 h-5" />
                    {sending ? "Sender bestilling..." : "Bestill Rstrøm Privat"}
                  </button>
                  <p className="text-[11px] text-slate-500 text-center">
                    Uforpliktende frem til oppstart – du har alltid 14 dagers angrerett.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BliKundePage;
